'use client';

import { useState } from 'react';
import { navbarLinkToUrl } from '../cms/helpers';
import { pushDataLayer } from '../cms/pushDataLayer';
import { FeLink } from '../FeLink';
import { CmsNavigation } from '$/(cms)/_modules/cmsService/types';
import { useClientContext } from '$/modules/context/client';

/**
 * This is the adapter for the footer menu when used with legacy layout.
 *
 * Key differences from TheFooter.tsx:
 * 1. Container Layout:
 *    - Uses `flex-wrap items-start` to prevent layout shifts when content expands
 *    - Different responsive gap strategy: `gap-5 sm:gap-10 md:gap-10` vs `gap-5 md:gap-20 lg:gap-10`
 *
 * 2. Column Distribution:
 *    - Mobile: `basis-5/12` (allows 2 columns per row)
 *    - Desktop: `md:flex-1 md:shrink-0` (equal distribution, prevents shrinking)
 *    - Original uses: `basis-5/12 md:basis-2/12 lg:flex-1 lg:basis-5/12`
 *
 * 3. Layout Stability:
 *    - Added `md:shrink-0` to prevent columns from shrinking when other columns expand
 *    - Added `md:min-w-0` to allow proper flex behavior
 *    - Removed width constraints that caused uneven distribution with fewer columns
 *
 * 4. Styling Changes:
 *    - Removed `text-primary-100` class from links for different text color treatment
 *    - Uses `px-2` padding instead of container-based padding
 *
 * 5. Responsive Behavior:
 *    - Better handling of fewer than 6 columns (fills remaining space evenly)
 *    - Prevents width shifting when chevron menus are expanded/collapsed
 */
export default function FooterMenuV2({ menu }: { menu: CmsNavigation }) {
  const { brand, langPrefix } = useClientContext();
  const [openLinks, setOpenLinks] = useState<string[]>([]);

  const toggleLink = (key: string) => {
    if (openLinks.includes(key)) {
      if (openLinks.length === 1) {
        setOpenLinks(openLinks.filter((item) => item !== key));
      } else {
        setOpenLinks([]);
      }
    } else {
      if (openLinks.length > 0) {
        setOpenLinks([key]);
      } else {
        setOpenLinks((prev) => [...prev, key]);
      }
    }
  };

  return (
    <div
      className="flex w-full basis-9/12 flex-wrap gap-5 px-2 pt-6 sm:gap-10 md:flex-nowrap md:gap-10 md:pt-0"
      style={{ gridArea: 'menu' }}
    >
      {menu.items?.map((item, index) => {
        const sectionUrl = navbarLinkToUrl(item!, { brand, langPrefix });
        return (
          <div
            key={`${sectionUrl.text}.${sectionUrl.href}`}
            className="basis-5/12 md:min-w-0 md:flex-1 md:shrink-0"
          >
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <p className="text-white alpha-body-1 md:text-[18px]">
              {item!.label}
            </p>
            <ul className="mt-3 space-y-1">
              {item!.sublinks?.map((sublink, subIndex) => {
                const key = `${index}.${subIndex}`;
                const linkUrl = navbarLinkToUrl(sublink!, {
                  brand,
                  langPrefix,
                });
                return (
                  <li
                    key={key}
                    className="flex flex-col gap-2 transition-all duration-100"
                  >
                    <div className="flex flex-row items-center gap-1">
                      {sublink!.sublinks && sublink!.sublinks.length > 0 && (
                        <svg
                          className={openLinks.includes(key) ? 'rotate-90' : ''}
                          width="6"
                          height="8"
                          viewBox="0 0 6 8"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.63112 4.00017L0.564453 0.933507L1.267 0.230957L5.03622 4.00017L1.267 7.76939L0.564453 7.06684L3.63112 4.00017Z"
                            fill="#B7E7F2"
                          />
                        </svg>
                      )}
                      {!sublink!.sublinks || sublink!.sublinks.length === 0 ? (
                        <FeLink
                          href={sublink!.url || '#'}
                          className="text-sm"
                          onClick={() =>
                            pushDataLayer({
                              event: 'navigate',
                              component_type: 'footer',
                              component_title: `Footer_${sectionUrl.text}-${linkUrl.text}`,
                              action: 'click',
                              index: key,
                            })
                          }
                          rel="noreferrer"
                        >
                          {sublink!.label}
                        </FeLink>
                      ) : (
                        <button
                          type="button"
                          className="text-left text-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLink(key);
                          }}
                        >
                          {sublink!.label}
                        </button>
                      )}
                    </div>
                    {sublink!.sublinks && openLinks.includes(key) && (
                      <ul className="flex flex-col gap-1 pl-2.5">
                        {sublink!.sublinks.map((sublink, subIndex) => {
                          const sublinkUrl = navbarLinkToUrl(sublink!, {
                            brand,
                            langPrefix,
                          });
                          return (
                            <li
                              key={`${key}.${sublinkUrl.text}.${sublinkUrl.href}`}
                            >
                              <FeLink
                                href={sublinkUrl.href || '#'}
                                target={
                                  sublinkUrl.url_target === '_blank'
                                    ? '_blank'
                                    : '_self'
                                }
                                className="text-left text-sm"
                                onClick={() =>
                                  pushDataLayer({
                                    event: 'navigate',
                                    component_type: 'footer',
                                    component_title: `Footer_${sectionUrl.text}-${linkUrl.text}+${sublinkUrl.text}`,
                                    action: 'click',
                                    index: `${key}.${subIndex}`,
                                  })
                                }
                                rel="noreferrer"
                              >
                                {sublinkUrl.text}
                              </FeLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
